import gsap from "gsap";

import Flip from "gsap/Flip";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";

import Splide from "@splidejs/splide";
import LazyLoad from "vanilla-lazyload";

gsap.registerPlugin(Flip, ScrollTrigger, ScrollToPlugin);

// --------------------------------------------------
// 🙂 General
// --------------------------------------------------

const logo = document.querySelector('.logo');
const menuToggle = document.querySelector('.menu-toggle');
const menu = document.querySelector('.menu');

let mm = gsap.matchMedia();


// --------------------------------------------------
// 😴 Lazy Loading
// --------------------------------------------------

let fadeLoad = (element) => {
  // Fade the image in
  gsap.to(element, {
    autoAlpha: 1,
    duration: .25,
    ease: 'power1.inOut',
  })
}

// Set up the lazy loading instance
new LazyLoad({
  elements_selector: "[data-lazy]",
  threshold: 1000,
  callback_loaded: (element) => {

    // Fade the image in
    fadeLoad(element);

    // If the element is inside a horizontal scroller, we want to load everything inside the scroller
    let horizontalScroller
    if(horizontalScroller = element.closest('.splide')) {
      let images = horizontalScroller.querySelectorAll('[data-lazy]');
      images.forEach(image => {
        LazyLoad.load(image, {
          callback_loaded: (element) => {
            fadeLoad(element);
          }
        })
      });
    }

  }
});

// --------------------------------------------------
// 📏 Grid
// --------------------------------------------------

const grid = document.querySelector('.guides');
const gridInner = document.querySelector('.guides .inner');

// Set up a tween that animates the grid in and out
let toggleGrid = gsap.to(grid, {
  duration: .5,
  ease: 'power2.inOut',
  scaleY: 1,
  autoAlpha: 1,
  paused: true
}).reverse();

// Check if the grid was enabled before
if (localStorage.getItem('debug') == 'true') {
  toggleGrid.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener('keydown', e => {
  if (e.key === 'g' && e.ctrlKey) {

    // Toggle the grid when ⌃G is pressed
    if(toggleGrid.reversed()) {
      toggleGrid.play();
    }
    else {
      if(gridInner.classList.contains('inner--wide')) {
        toggleGrid.reverse()
        gsap.delayedCall(toggleGrid.duration(), () => {
          gridInner.classList.remove('inner--wide');
        })
      }
      else {
        gridInner.classList.add('inner--wide');
      }
    }

    // Save the state in local storage
    localStorage.setItem('debug', !toggleGrid.reversed());
  }
})

window.addEventListener('resize', () => {
  grid.dataset.windowWidth = window.innerWidth;
})

// --------------------------------------------------
// ✨ Home load animation
// --------------------------------------------------

if(document.documentElement.dataset.template === 'home') {

  let hero = document.querySelector('.hero-home');
  let heroVideo = document.querySelector('.hero-home__video');
  let heroText = document.querySelector('.hero-home__text');
  let heroArrow = document.querySelector('.hero-home__arrow');

  document.documentElement.classList.add('loading');

  let loadAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'power1.inOut',
      duration: 1
    }
  })

  .set(window, {
    scrollTo: 0
  })

  .fromTo(heroVideo, {
    autoAlpha: 0,
  },{
    autoAlpha: parseFloat(heroVideo.dataset.opacity),
    duration: 1.5,
  })

  .to([heroText, logo, menuToggle],{
    autoAlpha: 1,
  }, '-=25%')

  .fromTo(heroArrow, {
    y: -40
  }, {
    y: 0,
    ease: 'power3.out',
    duration: 2,
    autoAlpha: 1
  })

  window.addEventListener('DOMContentLoaded', () => {
    loadAnimation.play();
    document.documentElement.classList.remove('loading');
  })

}

else {

  // document.documentElement.classList.add('loading');

  // let loadAnimation = gsap.timeline({
  //   paused: true,
  //   defaults: {
  //     ease: 'power1.inOut',
  //     duration: 1
  //   }
  // })

  // .fromTo('main', {
  //   autoAlpha: 0,
  // },{
  //   autoAlpha: 1,
  // })
  
  // window.addEventListener('DOMContentLoaded', () => {
  //   loadAnimation.play();
  //   document.documentElement.classList.remove('loading');
  // })

}

// --------------------------------------------------
// 🌓 Dark/light logo color
// --------------------------------------------------

const darkElements = document.querySelectorAll('[data-dark]');
let darkLightScrollTrigger;

darkElements.forEach(element => {

  darkLightScrollTrigger = gsap.fromTo(logo, {
    color: 'rgb(0, 0, 0)'
  },{
    duration: .25,
    ease: 'power1.inOut',
    color: 'rgba(255, 255, 255)',
    scrollTrigger: {
      toggleActions: 'play reverse play reverse',
      trigger: element,
      start: () => 'top ' + Math.round(logo.offsetTop + (logo.clientHeight / 2)) + 'px',
      end: () => 'bottom ' + Math.round(logo.offsetTop + (logo.clientHeight / 2)) + 'px',
    }
  })

})

// --------------------------------------------------
// ↕️ Arrows
// --------------------------------------------------

const verticalArrows = document.querySelectorAll('.arrow');

verticalArrows.forEach(arrow => {
  let arrowAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'power1.inOut',
      duration: .5
    }
  })
  .to(arrow.querySelector('path:first-child'), {
    scaleY: 1.2,
    transformOrigin: 'bottom'
  }, 0)
  .to(arrow, {
    yPercent: 7,
    transformOrigin: 'bottom'
  }, 0)
  arrow.addEventListener('mouseenter', () => {
    arrowAnimation.play();
  })
  arrow.addEventListener('mouseleave', () => {
    arrowAnimation.reverse();
  })
})

const horizontalArrows = document.querySelectorAll('.horizontal-arrow');

horizontalArrows.forEach(arrow => {
  let arrowAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'power1.inOut',
      duration: .5
    }
  })
  .to(arrow.querySelector('path:first-child'), {
    scaleX: 1.2,
    transformOrigin: arrow.classList.contains('horizontal-arrow--next') ? 'right' : 'left'
  }, 0)
  .to(arrow.querySelector('svg'), {
    xPercent: arrow.classList.contains('horizontal-arrow--next') ? 7 : -7,
    transformOrigin: arrow.classList.contains('horizontal-arrow--next') ? 'right' : 'left'
  }, 0)
  arrow.addEventListener('mouseenter', () => {
    arrowAnimation.play();
  })
  arrow.addEventListener('mouseleave', () => {
    arrowAnimation.reverse();
  })
})

// --------------------------------------------------
// 🌊 Menu
// --------------------------------------------------

const menuToggleText = document.querySelector('.menu-toggle__text');
const menuToggleWaves = document.querySelectorAll('.menu-toggle .wave');
const menuToggleWave1 = document.querySelector('.menu-toggle .wave--1');
const menuToggleWave2 = document.querySelector('.menu-toggle .wave--2');
const menuToggleWave3 = document.querySelector('.menu-toggle .wave--3');
const menuToggleClose = document.querySelector('.menu-toggle .close');
const mainMenuItems = document.querySelectorAll('.main-menu__item');
const metaMenuItems = document.querySelectorAll('.meta-menu .button');

// Hide menu toggle text on scroll

if(document.documentElement.dataset.template === 'home') {

  gsap.to(menuToggleText, {
    autoAlpha: 0,
    xPercent: 100,
    ease: 'power2.inOut',
    scrollTrigger: {
      start: 0,
      end: 100,
      scrub: .5
    }
  })

}

// Menu toggle hover

menuToggle.addEventListener('mouseenter', () => {

  let menuToggleAnimation = gsap.timeline({
    defaults: {
      ease: 'power1.out',
    }
  })
  
  menuToggleAnimation.to(menuToggleWave1, {
    x: -16
  }, 0)
  
  .to(menuToggleWave2, {
    x: 16
  }, 0)
  
  .to(menuToggleWave3, {
    x: -16
  }, 0)

})

menuToggle.addEventListener('mouseleave', () => {

  let menuToggleAnimation = gsap.timeline({
    defaults: {
      ease: 'power1.inOut',
      duration: 1
    }
  })
  
  menuToggleAnimation.to(menuToggleWaves, {
    x: 0
  }, 0)

})

let logoColor = gsap.getProperty(logo, 'color');
let menuToggleTextOpacity = gsap.getProperty(menuToggleText, 'opacity');

menuToggle.addEventListener('click', () => {

  let expanded = menuToggle.getAttribute('aria-expanded') === 'true';

  if(expanded) {

    menuToggle.setAttribute('aria-expanded', 'false');

    let menuAnimation = gsap.timeline({
      defaults: {
        ease: 'power1.inOut',
      }
    })
    .fromTo(menuToggleClose, {
      scale: 1,
      transformOrigin: 'center'
    }, {
      scale: .25,
      autoAlpha: 0,
    }, 0)
    .to(menu, {
      autoAlpha: 0,
    }, 0)
    .to(menuToggleWave1, {
      x: -16,
      autoAlpha: 1
    }, 0)
    .to(menuToggleWave2, {
      x: 16,
      autoAlpha: 1
    }, 0)
    .to(menuToggleWave3, {
      x: -16,
      autoAlpha: 1
    }, 0)
    .to('.back-button', {
      autoAlpha: 1,
    }, 0)

    if(logoColor === 'rgb(255, 255, 255)') {
      menuAnimation.to(logo, {
        color: 'rgb(255, 255, 255)'
      }, 0)
    }

    if(menuToggleTextOpacity === 1) {
      menuAnimation.fromTo(menuToggleText, {
        autoAlpha: 0
      }, {
        autoAlpha: 1
      }, 0)
    }

    darkLightScrollTrigger.scrollTrigger.enable();

  }
  else {

    logoColor = gsap.getProperty(logo, 'color');
    menuToggleTextOpacity = gsap.getProperty(menuToggleText, 'opacity');

    menuToggle.setAttribute('aria-expanded', 'true');

    let menuAnimation = gsap.timeline({
      defaults: {
        ease: 'power1.inOut',
      }
    })
    .to(menu, {
      autoAlpha: 1,
    }, 0)
    .to(menuToggleWaves, {
      x: 0,
      autoAlpha: 0
    }, 0)
    .fromTo(menuToggleClose, {
      scale: .25,
      transformOrigin: 'center'
    }, {
      scale: 1,
      autoAlpha: 1,
    }, 0)
    .fromTo(mainMenuItems, {
      autoAlpha: 0,
    },{
      autoAlpha: 1,
      stagger: .075,
      clearProps: 'all'
    }, '-=25%')
    .fromTo([metaMenuItems], {
      autoAlpha: 0,
    },{
      autoAlpha: 1,
      clearProps: 'all'
    }, '-=50%')
    .to('.back-button', {
      autoAlpha: 0,
    }, 0)

    if(window.matchMedia(('(min-width: 1000px)')).matches) {
      if(logoColor === 'rgb(255, 255, 255)') {
        menuAnimation.to(logo, {
          color: 'rgb(0, 0, 0)'
        }, 0)
      }
    }

    if(menuToggleTextOpacity === 1) {
      menuAnimation.fromTo(menuToggleText, {
        autoAlpha: 1
      }, {
        autoAlpha: 0,
      }, 0)
    }

    darkLightScrollTrigger.scrollTrigger.disable(false);

  }
})

// --------------------------------------------------
// 🧑‍🎨 Artists list
// --------------------------------------------------

const artistsList = document.querySelector('.artists-list');
const artistsImage = document.querySelector('.artists__image');
const artists = document.querySelectorAll('.artist-link');
const artistsSearch = document.querySelector('.artists-search');
const artistsSearchInner = document.querySelector('.artists-search__inner');
const artistsSearchToggle = document.querySelector('.artists-search__toggle');
const artistsSearchInput = document.querySelector('.artists-search__input');
const artistsListEmpty = document.querySelector('.artists-list__empty');
const artistsListReset = document.querySelector('.artists-list__reset');

if(artistsList) {

  const xTo = gsap.quickTo(artistsImage, "x", {duration: 0.5, ease: "power3.out"})
  const yTo = gsap.quickTo(artistsImage, "y", {duration: 0.5, ease: "power3.out"});

  // Set initial values to center
  xTo(window.innerWidth / 2);
  yTo(window.innerHeight / 2);

  document.addEventListener('mousemove', function(e) {
    xTo(e.clientX);
    yTo(e.clientY);
  });

  artists.forEach(artist => {

    let artistImage = artist.querySelector('.artist-link__image');

    if(artistImage) {

      artistsImage.appendChild(artistImage);

      artist.addEventListener('mouseenter', () => {

        artistsImage.querySelectorAll('img').forEach(image => {

          if(image !== artistImage) {
            gsap.set(image, {
              autoAlpha: 0,
            })
          }
          else {
            gsap.set(image, {
              autoAlpha: 1,
            })
          }

        })

      })

    }
    else {

      artist.addEventListener('mouseenter', () => {

        artistsImage.querySelectorAll('img').forEach(image => {

          gsap.set(image, {
            autoAlpha: 0,
          })

        })

      })

    }

    artist.addEventListener('mouseenter', () => { 

      artistsSearchAnimation.play();
  
    })
  
    artist.addEventListener('mouseleave', () => { 
  
      artistsSearchAnimation.reverse();
  
    })

  })

  let artistsSearchAnimation = gsap.timeline({
    paused: true,
  })
  .fromTo(artistsImage, {
    scale: .8
  }, {
    scale: 1,
    autoAlpha: 1,
    ease: 'power3.out',
    duration: .5
  })

  const closeArtistsSearch = () => {

    artistsSearchInput.value = '';

    artistsSearchInput.dispatchEvent(new Event('input'));

    artistsSearchToggle.setAttribute('aria-expanded', 'false');
    artistsSearchInput.setAttribute('aria-expanded', 'false');

    gsap.timeline({
      defaults: {
        ease: 'power3.inOut',
        duration: 1
      }
    })
    .to(artistsSearchToggle.querySelector('svg:nth-child(1)'), {
      autoAlpha: 1,
      duration: .2
    })
    .to(artistsSearchToggle.querySelector('svg:nth-child(2)'), {
      autoAlpha: 0,
      duration: .2
    }, '<')
    .to(artistsSearchInner, {
      width: '5.5rem',
    }, 0)

  }

  const openArtistsSearch = () => {

    artistsSearchToggle.setAttribute('aria-expanded', 'true');
    artistsSearchInput.setAttribute('aria-expanded', 'true');
    artistsSearchInput.focus();

    gsap.timeline({
      defaults: {
        ease: 'power3.inOut',
        duration: 1
      }
    })
    .fromTo(artistsSearchInner, {
      width: () => '5.5rem',
    },{
      width: () => artistsSearch.clientWidth,
      onComplete: () => {
        gsap.set(artistsSearchInput, {
          width: '100%'
        })
      }
    }, 0)
    .to(artistsSearchToggle.querySelector('svg:nth-child(1)'), {
      autoAlpha: 0,
      duration: .2
    }, '<')
    .to(artistsSearchToggle.querySelector('svg:nth-child(2)'), {
      autoAlpha: 1,
      duration: .2
    }, '<')

  }

  artistsSearchInput.addEventListener('focus', () => {
    openArtistsSearch();
  })

  artistsSearchToggle.addEventListener('click', () => {
    closeArtistsSearch();
  })

  artistsSearchInput.addEventListener('blur', () => {
    if(artistsSearchInput.value === '') {
      closeArtistsSearch(); 
    }
  })


  artistsSearchInput.addEventListener('input', () => {

    let searchValue = artistsSearchInput.value.toLowerCase();

    artists.forEach(artist => {

      let artistText = artist.textContent.toLowerCase();

      if(artistText.includes(searchValue)) {

        gsap.set(artist, {
          display: 'grid'
        })

        artist.setAttribute('aria-hidden', 'false');

      } else {

        gsap.set(artist, {
          display: 'none'
        })

        artist.setAttribute('aria-hidden', 'true');

      }

    })

    if(artists.length === document.querySelectorAll('.artist-link[aria-hidden="true"]').length) {

      gsap.set(artistsListEmpty, {
        display: 'block'
      })

      artistsListEmpty.setAttribute('aria-hidden', 'false');

    } else {

      gsap.set(artistsListEmpty, {
        display: 'none'
      })

      artistsListEmpty.setAttribute('aria-hidden', 'true');

    }


  })

  artistsListReset.addEventListener('click', () => {

    closeArtistsSearch();

  })

}

// --------------------------------------------------
// Sliders
// --------------------------------------------------

let sliders = document.querySelectorAll('.images-block__slider');

sliders.forEach(slider => {

  let splide = new Splide(slider, {
    type: 'loop',
    speed: 800,
    perPage: 1,
    dragMinThreshold: 0,
    flickPower: 20000,
    flickMaxPages: 1,
    pagination: true,
    arrows: false,
  });

  splide.mount();

  // Arrows

  let arrowPrev = slider.querySelector('.slider__arrow--prev');
  let arrowNext = slider.querySelector('.slider__arrow--next');

  if(arrowPrev && arrowNext) {
    
    arrowPrev.addEventListener('click', () => {
      splide.go('<');
    })

    arrowNext.addEventListener('click', () => {
      splide.go('>');
    })

  }

})

// --------------------------------------------------
// Horizontal Scrollers
// --------------------------------------------------

let horizontalScrollers = document.querySelectorAll('.horizontal-scroller');

horizontalScrollers.forEach(horizontalScroller => {

  let slides = horizontalScroller.querySelectorAll('.horizontal-scroller__item');

  if(slides.length < 2) {

    gsap.set(horizontalScroller.querySelector('.horizontal-scroller__arrows'), {
      autoAlpha: 0
    })

    gsap.set(horizontalScroller, {
      autoAlpha: 1
    })

  }
  else {

    let splide = new Splide(horizontalScroller, {
      type: 'loop',
      speed: 800,
      perPage: 1,
      dragMinThreshold: 0,
      flickPower: 20000,
      flickMaxPages: 1,
      pagination: false,
      arrows: false,
      breakpoints: {
        999: {
          pagination: true
        }
      }
    });
  
    splide.mount();
  
    // Arrows
  
    let arrowPrev = horizontalScroller.querySelector('.horizontal-scroller__arrow--prev');
    let arrowNext = horizontalScroller.querySelector('.horizontal-scroller__arrow--next');

  
    if(arrowPrev && arrowNext) {
      
      arrowPrev.addEventListener('click', () => {
        splide.go('<');
      })
  
      arrowNext.addEventListener('click', () => {
        splide.go('>');
      })
  
    }

  }

})

// --------------------------------------------------
// Carousels
// --------------------------------------------------

let carousels = document.querySelectorAll('.carousel');

carousels.forEach(carousel => {

  let slides = carousel.querySelectorAll('.splide__slide');

  let splide = new Splide(carousel, {
    type: 'loop',
    speed: 800,
    perPage: 4,
    perMove: 1,
    breakpoints: {
      1599: {
        perPage: 3
      },
      999: {
        perPage: 1,
        pagination: true
      }
    },
    gap: 'var(--grid-gap)',
    dragMinThreshold: 0,
    flickPower: 20000,
    flickMaxPages: 1,
    pagination: false,
    arrows: false,
  });

  // Arrows

  let arrowPrev = carousel.querySelector('.carousel__arrow--prev');
  let arrowNext = carousel.querySelector('.carousel__arrow--next');

  if(arrowPrev && arrowNext) {
    
    arrowPrev.addEventListener('click', () => {
      if(carousel.classList.contains('is-initialized')) {
        splide.go('<');
      }
    })

    arrowNext.addEventListener('click', () => {
      if(carousel.classList.contains('is-initialized')) {
        splide.go('>');
      }
    })

  }

  const dynamicMount = () => {

    let perPage = 4;

    if(window.matchMedia('(max-width: 999px)').matches) {
      perPage = 1;
    }
    else if(window.matchMedia('(max-width: 1599px)').matches) {
      perPage = 3;
    }


    if(perPage >= slides.length) {

      // Hide arrows
      gsap.set([arrowNext, arrowPrev], {
        autoAlpha: 0
      })

      // Unmount splide
      carousel.classList.add('is-destroyed');
      if(carousel.classList.contains('is-initialized')) {
        splide.destroy(true);
        carousel.classList.remove('is-initialized');
      }

    }
    else {

      // Show arrows
      gsap.set([arrowNext, arrowPrev], {
        autoAlpha: 1
      })

      // Mount splide
      carousel.classList.remove('is-destroyed');
      if(!carousel.classList.contains('is-initialized')) {
        splide.mount();
      }

    }

  }

  window.addEventListener('load', () => {
    dynamicMount()
  })

  window.addEventListener('resize', () => {
    dynamicMount()
  })


})

// // --------------------------------------------------
// // Page title
// // --------------------------------------------------

// let pageTitle = document.querySelector('.page-title');

// if(pageTitle) {

//   gsap.to(pageTitle, {
//     autoAlpha: 0,
//     scrollTrigger: {
//       trigger: '.footer',
//       start: 'top bottom',
//       end: 'bottom bottom',
//       scrub: 1
//     }
//   })

// }

// --------------------------------------------------
// 🎳 Filter
// --------------------------------------------------

const filterWrap = document.querySelector('.filter');
if(filterWrap) {

  const filterItems = filterWrap.querySelectorAll('.filter__item');
  const mobileFilter = document.querySelector('.events-header__mobile-filter select');
  const mobileFilterOptions = mobileFilter.querySelectorAll('option');
  const mobileArchiveToggle = document.querySelector('.events-header__mobile-archive-toggle select');
  const events = document.querySelectorAll('.event-link');

  mobileArchiveToggle.addEventListener('change', () => {
    let activeFilter = mobileArchiveToggle.querySelector('option:checked');
    window.location.href = activeFilter.value;
  })

  mobileFilterOptions.forEach(mobileFilterOption => {
    let eventsWithCategory = document.querySelectorAll('.event-link[data-category="' + mobileFilterOption.dataset.category + '"]');
    if(mobileFilterOption.dataset.category !== 'all' && eventsWithCategory.length === 0) {
      mobileFilterOption.remove();
    }
  });

  mobileFilter.addEventListener('change', () => {

    let activeFilter = mobileFilter.querySelector('option:checked');

    filter(true, activeFilter);

  })

  filterItems.forEach(filterItem => {

    let eventsWithCategory = document.querySelectorAll('.event-link[data-category="' + filterItem.dataset.category + '"]');
    
    if(filterItem.dataset.category !== 'all' && eventsWithCategory.length === 0) {
      filterItem.remove();
    }
    else {
      filterItem.addEventListener('click', (e) => {

        filterItems.forEach(otherFilterItem => {
          otherFilterItem.setAttribute('aria-pressed', 'false');
        })
        filterItem.setAttribute('aria-pressed', 'true');
  
        filter(true, filterItem);
  
        e.preventDefault();
      });
    }

  });
  
  function filter(animate = true, activeFilter) {
  
    let eventsState = Flip.getState(events);
    let startHeight = gsap.getProperty(".events-list", "height");
  
    events.forEach(event => {
      
      if(event.dataset.category === activeFilter.dataset.category || activeFilter.dataset.category === 'all') {
        event.setAttribute('aria-hidden', 'false');
      }
      else {
        event.setAttribute('aria-hidden', 'true');
      }

    });

    if(animate) {

      let endHeight = gsap.getProperty(".events-list", "height");
  
      let flip = Flip.from(eventsState, {
        duration: 0.5,
        ease: "power3.inOut",
        absolute: true,
        simple: true,
        onEnter: elements => gsap.fromTo(elements, {opacity: 0}, {opacity: 1, duration: .5, ease: "power3.inOut"}),
        onLeave: elements => gsap.to(elements, {opacity: 0, duration: .5, ease: "power3.inOut"})
      });
      
      flip.fromTo(".events-list", {
        height: startHeight
      }, {
        height: endHeight,
        overwrite: true,
        clearProps: "height",
        ease: "power3.inOut",
        duration: flip.duration()
      }, 0);

    }
  
    if(activeFilter.dataset.category !== 'all') {
      history.replaceState(null, null, document.documentElement.dataset.url + '#' + activeFilter.dataset.category.toLowerCase());
    }
    else {
      history.replaceState(null, null, document.documentElement.dataset.url);
    }
  }

  if(window.location.hash) {

    let hash = window.location.hash.substr(1);

    filterItems.forEach(filterItem => {

      if(filterItem.dataset.category.toLowerCase() === hash) {
        filterItem.setAttribute('aria-pressed', 'true');
      }
      else {
        filterItem.setAttribute('aria-pressed', 'false');
      }

    })

    mobileFilterOptions.forEach(mobileFilterOption => {

      if(mobileFilterOption.dataset.category.toLowerCase() === hash) {
        mobileFilterOption.selected = true;
      }
      else {
        mobileFilterOption.selected = false;
      }

    })

    filter(false, document.querySelector('.filter__item[aria-pressed="true"]'));

  }

}